import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const textColor = isScrolled
    ? "text-foreground-700"
    : "text-background-50";
  const textHover = isScrolled
    ? "hover:text-foreground-950"
    : "hover:text-background-50/80";
  const brandColor = isScrolled
    ? "text-foreground-900"
    : "text-background-50";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/60"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${isScrolled ? "text-foreground-800" : "text-background-50"}`}
            aria-label="Toggle menu"
          >
            <i className={`ri-${isMobileMenuOpen ? "close" : "menu"}-line text-xl`}></i>
          </button>

          {/* Left nav links - desktop */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className={`text-sm font-semibold ${textColor} ${textHover} transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5`}>
              <i className="ri-home-4-line text-sm"></i>
              Home
            </Link>

            <Link to="/shopping-guide" className={`text-sm font-medium ${textColor} ${textHover} transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5`}>
              <i className="ri-shopping-bag-3-line text-sm"></i>
              Shopping Guide
            </Link>

            <a href="#categories" className={`text-sm font-medium ${textColor} ${textHover} transition-colors whitespace-nowrap cursor-pointer`}>
              Categories
            </a>

            {/* Categories dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-1 text-sm font-medium ${textColor} ${textHover} transition-colors whitespace-nowrap cursor-pointer`}
              >
                Shop by Category
                <i className={`${isDropdownOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-xs transition-transform`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-2 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-100/60 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a href="#articles" className={`text-sm font-medium ${textColor} ${textHover} transition-colors whitespace-nowrap cursor-pointer`}>
              Guides
            </a>
          </div>

          {/* Center brand */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-grid-line text-white text-sm"></i>
            </div>
            <span className={`text-base font-semibold whitespace-nowrap hidden sm:block ${brandColor}`}>
              ACBuySpreadsheet
            </span>
          </Link>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60">
          <div className="px-4 py-4 flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-2 py-3 px-3 rounded-lg text-sm font-semibold text-foreground-700 hover:bg-primary-100/60 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line text-base"></i>
              Home
            </Link>

            <Link
              to="/shopping-guide"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-2 py-3 px-3 rounded-lg text-sm font-semibold text-foreground-700 hover:bg-primary-100/60 transition-colors cursor-pointer"
            >
              <i className="ri-shopping-bag-3-line text-base"></i>
              Shopping Guide
            </Link>

            <a
              href="#categories"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-2 py-3 px-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-primary-100/60 transition-colors cursor-pointer"
            >
              <i className="ri-apps-2-line text-base"></i>
              Categories
            </a>

            <a
              href="#articles"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-2 py-3 px-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-primary-100/60 transition-colors cursor-pointer"
            >
              <i className="ri-book-open-line text-base"></i>
              Guides
            </a>

            <div className="border-t border-background-200/60 my-2"></div>

            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider px-3 py-1">
              Shop by Category
            </p>

            <div className="grid grid-cols-2 gap-1 px-1">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-2 py-2.5 px-2 rounded-lg text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                  {cat.name}
                </a>
              ))}
            </div>

            <div className="border-t border-background-200/60 my-2"></div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 mx-3 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}